// Shared by Cloud Functions and the frontend (imported there as @shared).
// College recruiting's rules: high school recruits are pursued during the
// regular season and join as freshmen at the offseason's development step
// (league/recruiting.js has the pure server side).
//
// Each day (preseason included) a coach has CONTACTS_PER_DAY contacts:
// a pitch (the portal's pitch types) or a scouting visit, which narrows
// the recruit's rating ranges. Scholarship offers don't use contacts, but
// a team can have only offerLimit() open offers. A recruit commits only to
// a school that offered him; the last regular-season day is signing day.

import {PITCH_TYPES} from "./portal.js";

export const CONTACTS_PER_DAY = 3;
// Rating range width at each scouting level (0 = never scouted). A range
// at one level always sits inside the range at the level before.
export const RANGE_WIDTHS = [24, 12, 6, 2];
export const MAX_SCOUT_LEVEL = RANGE_WIDTHS.length - 1;
// Share of a class at each star rating or better (by scouting services'
// read of current ability, not hidden potential).
export const STAR_SHARES = [[5, 0.04], [4, 0.16], [3, 0.45], [2, 0.8],
  [1, 1]];
// The days before signing day when recruits may commit.
export const COMMIT_FROM_DAY = 5;
// Pulling a committed recruit's scholarship: allowed this many times a
// season (before signing day). It costs the school this much of every
// other recruit's interest for the rest of the season (about one interest
// level), and the recruit won't consider it again.
export const PULL_PENALTY = 0.15;
export const CONTACT_TYPES = [
  ...PITCH_TYPES,
  {key: "scout", label: "Scout", text: "A visit to watch the recruit play."},
];

/**
 * A senior who graduates at the end of this season (a redshirt senior
 * stays for a fifth year).
 * @param {object} player
 * @return {boolean}
 */
export function graduating(player) {
  return player.year >= 4 && !player.redshirt;
}

/**
 * Whether coaches may recruit now: a college league's preseason and
 * regular season.
 * @param {object} league
 * @return {boolean}
 */
export function recruitingOpen(league) {
  return league?.type !== "pro" &&
    ["preseason", "regular"].includes(league?.season?.phase);
}

/**
 * Scholarships a team can still give for next season: roster spots not
 * taken by returning players or recruits who already committed.
 * @param {object} team team document (players, recruits)
 * @param {number} rosterSize
 * @return {number}
 */
export function openScholarships(team, rosterSize) {
  const returning = Object.values(team.players ?? {})
      .filter((p) => !graduating(p)).length;
  return Math.max(0, rosterSize - returning - (team.recruits?.length ?? 0));
}

/**
 * How many open offers a team may have: twice its open scholarships, plus
 * one.
 * @param {number} scholarships
 * @return {number}
 */
export function offerLimit(scholarships) {
  return scholarships > 0 ? scholarships * 2 + 1 : 0;
}

/**
 * A class's score for the recruiting rankings: stars of its commits, the
 * best ones counting most.
 * @param {object[]} commits recruit cards
 * @return {number}
 */
export function classScore(commits) {
  return commits.map((c) => c.stars).sort((a, b) => b - a)
      .reduce((n, s, i) => n + s ** 2 * 0.9 ** i, 0);
}

/**
 * Recruits committed to a team at a position.
 * @param {object} team team document (recruits)
 * @param {number} pos
 * @return {number}
 */
export function commitsAt(team, pos) {
  return (team.recruits ?? []).filter((r) => r.pos1 === pos).length;
}

/**
 * A manager's position limits (scouting/{teamId}.limits) for this year's
 * class: {pos: most recruits wanted there}. Positions without a limit
 * are left out.
 * @param {object|null} scouting scouting/{teamId}
 * @param {number} year the class's year
 * @return {object}
 */
export function positionLimits(scouting, year) {
  return scouting?.year === year ? scouting.limits ?? {} : {};
}

/**
 * Whether a team has all the recruits it wants at a position.
 * @param {object} team
 * @param {object} limits from positionLimits
 * @param {number} pos
 * @return {boolean}
 */
export function positionFull(team, limits, pos) {
  const limit = limits?.[pos];
  return Number.isInteger(limit) && commitsAt(team, pos) >= limit;
}

/**
 * Whether a team already pulled a scholarship this season (and so can't
 * again). team.pulledScholarship: {year, playerId, day}.
 * @param {object} team
 * @param {number} year
 * @return {boolean}
 */
export function pulledThisSeason(team, year) {
  return team?.pulledScholarship?.year === year;
}
